// dropdown.tsx
import React, { useMemo } from 'react';
import './dropdown.css';

export interface DropdownOption {
    value: string;
    label: string;
}

interface DropdownProps {
    onChange: (selectedValue: string) => void;
    sortOrder?: 'asc' | 'desc' | 'none'; // How to sort the options
    labelFormatter?: (value: string) => string; // Optional function to format labels
    placeholder?: string;
    isDisabled?: boolean;
    // Optional parameters to automatically set dropdown as unique values in table
    tableData?: any[];
    columnName?: string;
    // Need to provide options if not passing table parameters
    options?: DropdownOption[];
    value?: string;
    // ui parameters
    height?: number;
    width?: number;
    className?: string;
}

const Dropdown: React.FC<DropdownProps> = ({ 
    options: providedOptions,
    value, 
    onChange, 
    placeholder, 
    isDisabled = false,
    tableData,
    columnName,
    labelFormatter,
    sortOrder = 'asc',
    height = 30,
    width = 200,
    className,
}) => {
    
    // Generate options from table data or use provided options
    const options = useMemo((): DropdownOption[] => {
        // If options are directly provided, use them
        if (providedOptions && providedOptions.length > 0) {
            return providedOptions;
        }

        // If table data and column name are provided, generate options
        if (tableData && columnName && tableData.length > 0) {
            // Extract unique values from the specified column
            const uniqueValues = Array.from(
                new Set(
                    tableData
                        .map(row => row[columnName])
                        .filter(val => 
                            val !== null && 
                            val !== undefined && 
                            val !== ''
                        )
                        .map(val => String(val))
                )
            );

            // Create dropdown options
            let options: DropdownOption[] = uniqueValues.map(val => ({
                value: val,
                label: labelFormatter ? labelFormatter(val) : val
            }));

            // Sort options if requested
            if (sortOrder !== 'none') {
                options.sort((a, b) => {
                    const comparison = a.label.localeCompare(b.label);
                    return sortOrder === 'asc' ? comparison : -comparison;
                });
            }

            return options;
        }

        // Return empty array if no valid data source
        return [];
    }, [providedOptions, tableData, columnName, labelFormatter, sortOrder]);

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = event.target.value;
        onChange(newValue);
    };
    
    // Use custom classname if provided
    const dropdownClassName = className || `nd-actionBar-dropdown ${!value ? 'nd-dropdown-empty' : ''}`;
    const dropdownStyle = { height: `${height}px`, width: `${width}px` };

    return (
        <select 
            value={value || ""} 
            onChange={handleChange}
            className={value ? dropdownClassName : `${dropdownClassName} nd-dropdown-placeholder`}
            disabled={isDisabled}
            style={dropdownStyle}
        >
            {placeholder &&(
                <option value="" className='nd-dropdown-placeholder'>
                    {placeholder}
                </option>
            )}

            {options.map((option) => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );
};

export default Dropdown;